import { isAxiosError } from "axios";
import api from "../api";
import type { PetitionResults, PetitionsParams } from "@/types/types.d";
import { literals } from "@/constants/literals";

export type ChangeRequestType = "DELETE" | "EDIT";
export type ChangeRequestStatus = "PENDING" | "APPROVED" | "REJECTED";

export const getImageUrl = async (imageId: number): Promise<string> => {
  try {
    const { data } = await api.get(`photos/${imageId}/download-url`);
    return data.url;
  } catch {
    throw new Error("Ha habido un problema");
  }
};

export const getNumberPhotosInGroup = async (groupId: string) => {
  try {
    const response = await api.get(`image-groups/${groupId}/images/count`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error("Ha habido un problema");
    }
  }
};

export const getAllStepsNumberPhotos = async (groupId: string | number) => {
  try {
    const response = await api.get(
      `image-groups/${groupId}/all-orders-images/count`,
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error("Ha habido un problema");
    }
  }
};

export const changeGroupMetadata = async (changeData: {
  imageId: number;
  imageGroupId: number;
  visible: boolean;
  description: string;
}) => {
  const { imageGroupId, visible, description, imageId } = changeData;

  const body = {
    visible: visible,
    description: description,
    imageId: imageId,
  };

  try {
    const response = await api.put(`image-groups/${imageGroupId}`, body);
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error("Ha habido un problema");
    }
  }
};

export const requestImageDeletion = async (imageId: number) => {
  try {
    const response = await api.post(
      `image-deletion-requests/images/${imageId}`,
    );

    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error("Ha habido un problema");
    }
  }
};

export const getImageDeletionRequestsByStatus = async (
  params: PetitionsParams,
): Promise<PetitionResults> => {
  try {
    const { data } = await api.get(`image-deletion-requests`, {
      params: params,
    });

    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    }
    throw new Error("Ha habido un problema");
  }
};

export const processImageDeletionRequest = async (
  requestId: number,
  status: "APPROVED" | "REJECTED",
) => {
  try {
    const { data } = await api.patch(`image-deletion-requests/${requestId}`, {
      status,
    });
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    }
    throw new Error("Ha habido un problema");
  }
};

export const getNumberPhotosInOrder = async (groupId: number | string) => {
  try {
    const response = await api.get(
      `image-groups/${groupId}/order-images/count`,
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    } else {
      throw new Error("Ha habido un problema");
    }
  }
};

export const editOrderAfterRequest = async (requestId: number, body: any) => {
  try {
    const response = await api.patch(
      `order-edition-requests/${requestId}`,
      body,
    );
    if (response.status === 204) {
      throw new Error(literals.notFound);
    }
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.message === literals.notFound) {
      throw error;
    } else if (isAxiosError(error) && error.response) {
      if (error instanceof Error && error.message === literals.notFound) {
        throw error;
      } else if (error.response.status === 404) {
        throw new Error(literals.notFound);
      }
      throw new Error(error.response.data.message);
    } else {
      throw new Error("Ha habido un problema");
    }
  }
};

export type UpdateOrderRequestApi = {
  orderId: number;
  imageId: number;

  customerOrderNoserv?: string | null;
  customerOrderServ?: string | null;

  orderIn?: string | null;
  orderOut?: string | null;

  serialIn?: string | null;
  serialOut?: string | null;

  customer?: string | null;
  sapWorkOrder?: string | null;
  document9r1?: string | null;

  packagingReference?: string | null;
  ownPackaging?: boolean | null;
  correctPackaging?: boolean | null;
  damagedPackaging?: boolean | null;
  ownPackagingOut?: boolean | null;
  iberiaPackagingOut?: boolean | null;
};

export const updateOrder = async (payload: UpdateOrderRequestApi) => {
  try {
    const { data } = await api.patch(`orders`, payload);
    return data;
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message);
    }
    throw new Error("Ha habido un problema");
  }
};
